import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  ProductTypeDto,
  ProductTypeEditDto
} from 'src/store/producttypes/producttype';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum ProductTypeTypes {
  FetchAll = 'productTypes/FetchAll',
  Add = 'productTypes/Add',
  Update = 'productTypes/Update',
  Delete = 'productTypes/Delete',
  Failure = 'productTypes/Failure'
}

export type ProductTypeAction =
  | { type: ProductTypeTypes.FetchAll; productTypes: ProductTypeDto[] }
  | { type: ProductTypeTypes.Add; productType: ProductTypeDto }
  | { type: ProductTypeTypes.Update; productType: ProductTypeDto }
  | { type: ProductTypeTypes.Delete; productType: ProductTypeDto }
  | { type: ProductTypeTypes.Failure; errors: any };

export const actionCreators = {
  fetchProductTypes: (productTypes: ProductTypeDto[]): ProductTypeAction => ({
    type: ProductTypeTypes.FetchAll,
    productTypes
  }),
  addProductType: (productType: ProductTypeDto): ProductTypeAction => ({
    type: ProductTypeTypes.Add,
    productType
  }),
  updateProductType: (productType: ProductTypeDto): ProductTypeAction => ({
    type: ProductTypeTypes.Update,
    productType
  }),
  deleteProductType: (productType: ProductTypeDto): ProductTypeAction => ({
    type: ProductTypeTypes.Delete,
    productType
  }),
  productTypeRequestFailure: (errors: any): ProductTypeAction => ({
    type: ProductTypeTypes.Failure,
    errors
  })
};

export const fetchProductTypes = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ProductTypeTypes.FetchAll));
    const { ok, data, error } = await getRequest<ProductTypeDto[]>(
      '/productTypes'
    );
    dispatch(requestEnd(ProductTypeTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchProductTypes(data))
      : dispatch(actionCreators.productTypeRequestFailure(error));
  };
};

export const createProductType = (productType: ProductTypeEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/productTypes',
      productType
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('productType.created')));
      return dispatch(
        actionCreators.addProductType({ ...productType, id: data })
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.productTypeRequestFailure(error));
  };
};

export const editProductType = (
  id: number,
  productType: ProductTypeEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/productTypes/${id}`, productType);

    if (ok) {
      dispatch(alertSuccess(i18n.t('productType.updated')));
      return dispatch(
        actionCreators.updateProductType(productType as ProductTypeDto)
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.productTypeRequestFailure(error));
  };
};

export const deleteProductType = (id: number, productType: ProductTypeDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/productTypes/${id}`,
      productType
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('productType.deleted')));
      return dispatch(actionCreators.deleteProductType(productType));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.productTypeRequestFailure(error));
  };
};
